import { CSSProperties, FC, useCallback, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useAppSelector, selectMetadata } from '@/store';
import { openNewTab } from '@/utils';
import { AppRoutes } from '@/constants';
import { Button } from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import { useLazyGetGdprSummaryQuery, GdprSummaryResponse } from '@/api/gdpr';
import { GdprSummaryModal } from './GdprSummaryModal';
import { CurrentPageLabel, useGdprControlsState } from '../GdprControlsContext';

/**
 * HotkeyIcon
 * Small ~16px keyboard-key style cap that displays a single hotkey character.
 * Color-themed to match the button it sits on (e.g. light grey for white buttons,
 * light red for the red Breach button).
 */
type HotkeyVariant = 'lightGrey' | 'lightRed' | 'lightTeal' | 'lightRedOutline';

const HOTKEY_VARIANT_STYLES: Record<HotkeyVariant, CSSProperties> = {
  lightGrey: { backgroundColor: '#F3F4F6', borderColor: '#D1D5DB', color: '#374151' },
  lightRed: { backgroundColor: '#FEE2E2', borderColor: '#FCA5A5', color: '#7F1D1D' },
  lightTeal: { backgroundColor: '#CCFBF1', borderColor: '#5EEAD4', color: '#0F766E' },
  lightRedOutline: { backgroundColor: '#FEF2F2', borderColor: '#FCA5A5', color: '#7F1D1D' },
};

const HotkeyIcon: FC<{ hotkey: string; variant: HotkeyVariant }> = ({ hotkey, variant }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 16,
      height: 16,
      borderWidth: 1,
      borderStyle: 'solid',
      borderRadius: 3,
      fontSize: 10,
      fontWeight: 600,
      lineHeight: 1,
      ...HOTKEY_VARIANT_STYLES[variant],
    }}
  >
    {hotkey}
  </span>
);

interface SidebarProps {
  onPageClick?: (pageNumber: number) => void; // Forwarded to GdprSummaryModal for page navigation
}

/**
 * CheckmarkIcon
 * White checkmark icon for the "Complete Check" button.
 */
const CheckmarkIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.3327 4L5.99935 11.3333L2.66602 8"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Sidebar
 * Right-hand panel for the V2 GDPR Check page (20vw wide).
 * Hosts:
 *  - case-reference link + document name (moved from ActionsPanel)
 *  - Complete Check button + GdprSummaryModal (moved from ActionsPanel)
 */
export const Sidebar: FC<SidebarProps> = ({ onPageClick }) => {
  const gdprDocMetadata = useAppSelector(selectMetadata);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get('from');
  const { documentId } = useParams<{ documentId: string }>();

  // GDPR action controls (Breach/Blank/Clear, navigation, progress) — published
  // from PdfViewer via context. Page-jumper now lives in the ActionsPanel.
  const {
    totalPages,
    isLoading: isControlsLoading,
    hasLabeledPages,
    labeledPageCount,
    currentLabeledPosition,
    currentPageLabel,
    onBreachClick,
    onBlankClick,
    onClearLabelsClick,
    onPrevLabeledPage,
    onNextLabeledPage,
  } = useGdprControlsState();

  // GDPR Summary Modal state — moved here with the Complete Check button it's triggered by.
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [summaryData, setSummaryData] = useState<GdprSummaryResponse | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [getGdprSummary] = useLazyGetGdprSummaryQuery();

  // Tracks which Assign-flag segment is currently hovered, so unselected segments can flash
  // a light grey background on mouse-over (selected segment keeps its solid scheme fill).
  const [hoveredRadioKey, setHoveredRadioKey] = useState<CurrentPageLabel | null>(null);

  const handleCaseClick = () => {
    if (!gdprDocMetadata?.caseId) return;
    if (fromParam === 'case') {
      navigate(`/${AppRoutes.CaseList}/${gdprDocMetadata.caseId}`);
    } else {
      openNewTab(`/${AppRoutes.CaseList}/${gdprDocMetadata.caseId}`);
    }
  };

  const handleCompleteCheck = useCallback(async () => {
    if (!documentId) return;

    setIsLoadingSummary(true);
    try {
      const result = await getGdprSummary({ documentId }).unwrap();
      setSummaryData(result);
      setIsSummaryModalVisible(true);
    } catch (error) {
      console.error('Failed to fetch GDPR summary:', error);
    } finally {
      setIsLoadingSummary(false);
    }
  }, [documentId, getGdprSummary]);

  return (
    <aside className="flex h-full w-[20vw] min-w-[320px] shrink-0 flex-col gap-2 border border-gray-200 bg-white p-6">
      {/* File (document) name — 18px semibold, single-line with ellipsis when it overflows
          the sidebar's content width. `title` exposes the full name on hover. */}
      <div className="truncate text-lg font-semibold text-gray-900" title={gdprDocMetadata?.documentName ?? undefined}>
        {gdprDocMetadata?.documentName ?? 'Document name not available'}
      </div>
      {/* Case reference — clickable link in fletchers-green-500, 14px. No directional arrow:
          behavior still differs based on `from` (same-tab vs. new-tab) but visually identical. */}
      <button
        onClick={handleCaseClick}
        className="text-fletchers-green-500 cursor-pointer text-left text-sm font-medium hover:underline disabled:cursor-default disabled:no-underline"
        disabled={!gdprDocMetadata?.caseId}
      >
        <span className="break-words">{gdprDocMetadata?.caseReference ?? 'Case reference not available'}</span>
      </button>

      {/* Divider — matches the ones above the progress block and Complete check.
          Parent flex uses gap-2 (8px), so my-4 gives 24px clear space above and below. */}
      <div className="!my-4 h-px w-full bg-gray-300" />

      {/* Inferred patient details — populated once BE returns name / DOB / NHS number.
          BE doesn't expose these fields anywhere I could find on the document or case
          metadata, so values are blank placeholders for now. */}
      {/* Wraps title + field rows so all gaps inside the section are 12px,
          independent of the outer aside's 8px gap. */}
      <div className="flex flex-col gap-3">
        <div className="text-sm font-semibold text-gray-900">Inferred patient details</div>
        {(['Name', 'Date of birth', 'NHS number'] as const).map((label) => (
          <div key={label} className="flex flex-col gap-0.5">
            <div className="text-sm text-gray-500">{label}</div>
            <div className="text-sm text-gray-900">—</div>
          </div>
        ))}
      </div>

      {/*
        Bottom group — pushed to the foot of the sidebar via mt-auto.
        Row 1: "Assign flag" label.
        Row 2: Breach / Blank / None segmented radio.
        Row 3: Review-progress text + bar (48px gap above to separate from the radio).
        Row 4: Previous / Next.
        Row 5: Page jumper.
        Row 6: Complete Check.
      */}
      <div className="mt-auto flex flex-col gap-2">
        {/* Divider — 1px grey line, 24px gap above (16px !my-4 + 8px parent gap-2) and below.
            Sits naturally inside the sidebar's 24px padding so the line itself ends 24px from the outer edges. */}
        <div className="!my-4 h-px w-full bg-gray-300" />

        {/* Review-progress block. Two states:
            - On a labelled page → "Reviewing flag X of Y" with bar at X/Y.
            - On an unlabelled page (or document has no labels) → "Manually reviewing" with bar at 0%.
            Hidden via `invisible` only when no document is loaded yet, so siblings don't shift. */}
        {(() => {
          const hasDocument = totalPages > 0;
          const onLabelledPage = currentLabeledPosition !== null && labeledPageCount > 0;
          const value = onLabelledPage ? (currentLabeledPosition as number) : 0;
          const max = Math.max(labeledPageCount, 1);
          return (
            <div className={classNames('flex flex-col gap-1', !hasDocument && 'invisible')}>
              <div className="text-left text-sm text-gray-700">
                {onLabelledPage ? (
                  <>
                    Reviewing flag <span className="font-semibold text-gray-900">{value}</span> of {labeledPageCount}
                  </>
                ) : (
                  'Manually reviewing'
                )}
              </div>
              <div
                role="progressbar"
                aria-valuenow={value}
                aria-valuemin={0}
                aria-valuemax={labeledPageCount}
                aria-label="Review progress"
                aria-hidden={!hasDocument}
                className="h-1 w-full overflow-hidden rounded-full bg-gray-300"
              >
                <div
                  className="bg-fletchers-green-500 h-full transition-all duration-200"
                  style={{ width: `${(value / max) * 100}%` }}
                />
              </div>
            </div>
          );
        })()}

        {/* Previous / Next labelled-page navigation. */}
        <div className="flex w-full gap-2">
          {/* Previous: hotkey badge right-aligned at 8px (mirrors the Breach/Blank/None pattern,
              flipped to the opposite edge since the conceptual direction is leftward). */}
          <Button
            onClick={onPrevLabeledPage}
            disabled={!hasLabeledPages}
            className="!h-[32px] flex-1 !border !border-gray-300 !bg-white !px-2 !text-black hover:!bg-gray-50"
          >
            <div className="relative w-full text-center">
              <span className="absolute inset-y-0 left-0 flex items-center">
                <i className="pi pi-arrow-left text-black" style={{ display: 'block', lineHeight: 1 }} />
              </span>
              <span>Previous</span>
              <span className="absolute inset-y-0 right-0 flex items-center">
                <HotkeyIcon hotkey="9" variant="lightGrey" />
              </span>
            </div>
          </Button>
          {/* Next: hotkey badge left-aligned at 8px, same pattern as Breach/Blank/None. */}
          <Button
            onClick={onNextLabeledPage}
            disabled={!hasLabeledPages}
            className="!h-[32px] flex-1 !border !border-gray-300 !bg-white !px-2 !text-black hover:!bg-gray-50"
          >
            <div className="relative w-full text-center">
              <span className="absolute inset-y-0 left-0 flex items-center">
                <HotkeyIcon hotkey="0" variant="lightGrey" />
              </span>
              <span>Next</span>
              <span className="absolute inset-y-0 right-0 flex items-center">
                <i className="pi pi-arrow-right text-black" style={{ display: 'block', lineHeight: 1 }} />
              </span>
            </div>
          </Button>
        </div>

        {/* 24px gap above (16px mt + 8px parent gap-2) to separate the navigation block
            from the Assign-flag group below. */}
        <div className="!mt-4 text-sm font-semibold text-gray-900">Assign flag</div>

        {/* Breach / Blank / None — segmented radio control.
            The whole control adopts the color scheme of the currently-selected option:
            if Breach is selected the entire control is red-themed (selected segment is solid red,
            unselected segments are white with red text/borders); same for Blank (teal) and None (grey).
            Detected-only labels map to 'none' in the published currentPageLabel. */}
        {(() => {
          type Scheme = {
            fill: string; // selected segment background
            border: string; // container border + segment dividers
            unselectedText: string;
            selectedText: string;
            badgeVariant: HotkeyVariant;
          };
          const schemes: Record<CurrentPageLabel, Scheme> = {
            breach: {
              fill: '#DD4F45',
              border: '#DD4F45',
              unselectedText: '#DD4F45',
              selectedText: 'white',
              badgeVariant: 'lightRed',
            },
            blank: {
              fill: '#13898D',
              border: '#13898D',
              unselectedText: '#13898D',
              selectedText: 'white',
              badgeVariant: 'lightTeal',
            },
            // None: matches the Previous/Next button styling — gray-300 border, black text.
            none: {
              fill: '#D1D5DB', // gray-300 — same as border, gives the selected segment a subtle grey wash
              border: '#D1D5DB',
              unselectedText: 'black',
              selectedText: 'black',
              badgeVariant: 'lightGrey',
            },
          };
          const scheme = schemes[currentPageLabel];

          const segments: Array<{ key: CurrentPageLabel; label: string; hotkey: string; onClick: () => void }> = [
            { key: 'breach', label: 'Breach', hotkey: '1', onClick: onBreachClick },
            { key: 'blank', label: 'Blank', hotkey: '2', onClick: onBlankClick },
            { key: 'none', label: 'None', hotkey: '3', onClick: onClearLabelsClick },
          ];

          return (
            <div
              role="radiogroup"
              className="flex flex-col overflow-hidden rounded-[4px]"
              style={{ border: `1px solid ${scheme.border}`, opacity: isControlsLoading ? 0.6 : 1 }}
            >
              {segments.map((seg, i) => {
                const isActive = currentPageLabel === seg.key;
                const isLast = i === segments.length - 1;
                const isHovered = hoveredRadioKey === seg.key;
                // Idle = white. Hover (unselected) = gray-50, like the Prev/Next buttons.
                // Selected segment ignores hover so the active fill stays solid.
                const backgroundColor = isActive ? scheme.fill : isHovered ? '#F9FAFB' : 'white';
                return (
                  <button
                    key={seg.key}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    onClick={seg.onClick}
                    disabled={isControlsLoading}
                    onMouseEnter={() => setHoveredRadioKey(seg.key)}
                    onMouseLeave={() => setHoveredRadioKey((prev) => (prev === seg.key ? null : prev))}
                    className="relative flex h-[32px] items-center justify-center px-2 text-sm font-medium transition-colors"
                    style={{
                      backgroundColor,
                      color: isActive ? scheme.selectedText : scheme.unselectedText,
                      borderBottom: isLast ? 'none' : `1px solid ${scheme.border}`,
                      cursor: isControlsLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <span className="absolute inset-y-0 left-0 flex items-center pl-1.5">
                      <HotkeyIcon hotkey={seg.hotkey} variant={scheme.badgeVariant} />
                    </span>
                    <span>{seg.label}</span>
                  </button>
                );
              })}
            </div>
          );
        })()}

        {/* Divider above Complete Check — same styling as the one above the progress block. */}
        <div className="!my-4 h-px w-full bg-gray-300" />

        <Button
          onClick={handleCompleteCheck}
          disabled={isLoadingSummary}
          className="!bg-fletchers-green-500 !border-fletchers-green-500 hover:!bg-fletchers-green-800 !h-[32px] !text-white"
          pt={{
            root: {
              className: 'bg-fletchers-green-500 hover:bg-fletchers-green-800 border-fletchers-green-500',
            },
            label: {
              className: 'font-medium text-sm',
            },
          }}
        >
          <div className="flex w-full items-center justify-center gap-2">
            <CheckmarkIcon />
            <span className="text-sm font-medium">Complete check</span>
          </div>
        </Button>
      </div>

      <GdprSummaryModal
        visible={isSummaryModalVisible}
        onHide={() => setIsSummaryModalVisible(false)}
        summaryData={summaryData}
        isLoading={isLoadingSummary}
        onPageClick={onPageClick}
        documentId={documentId}
        caseId={gdprDocMetadata?.caseId}
      />
    </aside>
  );
};
